import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, CalendarDays, ShieldCheck, Ban, CalendarX } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { VacationCalendar } from "./VacationCalendar";

const STATUS_STYLE = {
  pending: "text-amber-700 bg-amber-50",
  awaiting_confirmation: "text-terracotta bg-terracotta/10",
  approved: "text-emerald-700 bg-emerald-50",
  rejected: "text-red-700 bg-red-50",
  declined: "text-orange-700 bg-orange-50",
};

const STATUS_LABEL = {
  pending: "Pendiente de aprobación",
  awaiting_confirmation: "Esperando confirmación",
  approved: "Aprobada",
  rejected: "Rechazada por ti",
  declined: "No confirmada por el empleado",
};

const STATUS_ICON = {
  rejected: Ban,
  declined: CalendarX,
};

const STATUS_EXPLANATION = {
  rejected: "Tú rechazaste esta solicitud de vacaciones del empleado.",
  declined: "Le asignaste estas fechas y el empleado decidió no confirmarlas; no quedaron programadas.",
};

export default function VacationRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => {
    api.get("/vacation-requests").then(({ data }) => setRequests(data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/vacation-requests/${id}/approve`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reject = async () => {
    try {
      const { data } = await api.post(`/vacation-requests/${rejectTarget}/reject`, { reason: rejectReason || null });
      toast.success(data.message);
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">Control de Vacaciones</h1>
        <p className="text-charcoal/60 mt-1">Art. 76/78 LFT — solicitudes de empleados y asignaciones administrativas.</p>

        {loading && <p className="text-charcoal/60 mt-6">Cargando...</p>}

        {!loading && (
          <div className="mt-6">
            <VacationCalendar requests={requests} />
          </div>
        )}

        <h2 className="font-heading text-lg font-semibold text-charcoal mt-8 mb-1">Solicitudes</h2>
        <div className="space-y-3 mt-2">
          {requests.map((r) => {
            const StatusIcon = STATUS_ICON[r.status];
            return (
            <div key={r.id} data-testid="vacation-request-admin-row" className="bg-card border border-petroleum/10 rounded-xl p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <CalendarDays className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <Link to={`/admin/empleados/${r.employee_id}`} className="font-semibold text-charcoal hover:underline">{r.employee_name}</Link>
                  <p className="text-sm text-charcoal/60">{r.start_date} → {r.end_date} ({r.days_requested} días) · {r.requested_by === "admin" ? "Asignada por admin" : "Solicitud del empleado"}</p>
                  {r.balance_available !== undefined && (
                    <p data-testid={`vacation-balance-context-${r.id}`} className="text-xs font-mono-legal text-petroleum/70 flex items-center gap-1 mt-1">
                      <ShieldCheck className="w-3 h-3" /> Saldo {r.balance_year}: {r.balance_available} de {r.balance_earned} días (Art. 76/78 LFT — vacaciones dignas)
                    </p>
                  )}
                  {r.status === "rejected" && r.reviewed_reason && (
                    <p className="text-xs text-red-600/80 mt-1">Motivo: {r.reviewed_reason}</p>
                  )}
                  {(r.status === "rejected" || r.status === "declined") && (
                    <p className="text-xs text-charcoal/50 mt-1">{STATUS_EXPLANATION[r.status]}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${STATUS_STYLE[r.status]}`}>
                  {StatusIcon && <StatusIcon className="w-3 h-3" />}
                  {STATUS_LABEL[r.status] || r.status}
                </span>
                {r.status === "pending" && (
                  <>
                    <Button data-testid={`vacation-admin-approve-${r.id}`} size="sm" onClick={() => approve(r.id)} className="bg-emerald-600 hover:bg-emerald-700 rounded-full"><Check className="w-3.5 h-3.5" /></Button>
                    <Dialog open={rejectTarget === r.id} onOpenChange={(open) => { setRejectTarget(open ? r.id : null); if (!open) setRejectReason(""); }}>
                      <DialogTrigger asChild>
                        <Button data-testid={`vacation-admin-reject-${r.id}`} size="sm" className="bg-red-500 hover:bg-red-600 rounded-full"><X className="w-3.5 h-3.5" /></Button>
                      </DialogTrigger>
                      <DialogContent className="bg-bone">
                        <DialogHeader>
                          <DialogTitle>Rechazar solicitud de {r.employee_name}</DialogTitle>
                          <DialogDescription>El motivo quedará visible para el empleado en su historial de vacaciones.</DialogDescription>
                        </DialogHeader>
                        <Textarea
                          data-testid="vacation-reject-reason-input"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Motivo del rechazo (opcional, queda en el expediente)"
                          className="bg-white"
                        />
                        <Button data-testid="vacation-reject-confirm-button" onClick={reject} className="w-full bg-red-500 hover:bg-red-600 rounded-full">Confirmar rechazo</Button>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </div>
            );
          })}
          {!loading && requests.length === 0 && <p className="text-charcoal/50 text-sm">Aún no hay solicitudes de vacaciones.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}

